/**
 * Page de succès après Stripe Checkout — /retraites/reservation/succes
 *
 * Query params : `booking` (id) + `session_id` (Stripe).
 * On poll GET /api/retreats/... jusqu'à voir le paiement confirmé.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RetreatBookingSuccessPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking");
  const [status, setStatus] = useState("processing"); // processing | confirmed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!bookingId) return;
    let mounted = true;
    // Étape 1 : Réconciliation immédiate avec Stripe (couvre le cas où le webhook
    // n'est pas configuré / arrive en retard). Idempotent côté back.
    axios.post(`${API}/retreats/bookings/${bookingId}/reconcile`, {}, { withCredentials: true })
      .then((r) => {
        if (mounted && ["confirmed", "balance_due", "paid"].includes(r.data.status)) {
          setStatus("confirmed");
        }
      })
      .catch(() => { /* fallback sur le polling */ });

    // Étape 2 : Polling de secours si la réconciliation n'a pas suffi
    const poll = async () => {
      try {
        const res = await fetch(`${API}/me/retreats`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const b = (data.bookings || []).find((x) => x.booking_id === bookingId);
          if (b && ["confirmed", "balance_due", "paid"].includes(b.status)) {
            if (mounted) setStatus("confirmed");
            return;
          }
        }
      } catch { /* silent */ }
      if (mounted) setAttempts((a) => a + 1);
    };
    poll();
    const int = setInterval(() => {
      if (attempts < 20) poll();
      else clearInterval(int);
    }, 2500);
    return () => { mounted = false; clearInterval(int); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] to-[#1a1a2e] text-white flex items-center justify-center px-6">
      <div className="max-w-lg text-center py-24">
        {status === "processing" ? (
          <>
            <Loader2 className="w-12 h-12 text-[#F5D8A2] mx-auto animate-spin mb-6" />
            <h1 className="text-2xl font-light mb-3">On finalise ta réservation…</h1>
            <p className="text-white/60 text-sm">Ne ferme pas cette page. Ton paiement est en cours de validation.</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-[#F5D8A2]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#F5D8A2]" />
            </div>
            <h1 className="text-3xl font-light mb-3" data-testid="booking-success">Réservation confirmée</h1>
            <p className="text-white/70 mb-8 leading-relaxed">
              <Sparkles className="w-4 h-4 inline text-[#F5D8A2]" /> Ta place est bloquée. Tu as reçu un email de confirmation
              avec ta facture. Rendez-vous dans quelques semaines !
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/dashboard?tab=retraites">
                <Button className="bg-[#F5D8A2] text-[#0a0a1a] hover:bg-[#F5D8A2]/90 w-full font-semibold">
                  Voir ma réservation
                </Button>
              </Link>
              <Link to="/retraites">
                <Button variant="outline" className="border-white/20 text-white/70 hover:bg-white/5 w-full">
                  Retour aux retraites
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
